// MarketplaceIsland — self-contained, embeddable marketplace surface.
//
// Standalone marketplace (marketplace.localhost) mounts the pieces at
// the app root directly; there, `marketplaceHostOf(el)` finds the root
// (flagged `_marketplaceHost: true` on the app state) and reads
// `catalog` / `selectedItem` / `installed` from there.
//
// Other surfaces (canvas.localhost/marketplace, landing `/apps/...`)
// import this island and mount it anywhere. The island owns the
// catalog state locally so two embeds on the same page don't share
// `selectedItem`. The helper walks `el.parent` until it finds this
// island (flagged) — that becomes the host for all state reads/writes.

import catalog from '../data/catalog.js'

export const MarketplaceIsland = {
  // `flow: 'y'` already emits `display: flex` (see css-in-props/flex.js)
  // so `extends: 'Flex'` would be redundant.
  flow: 'y',
  flex: '1',
  width: '100%',
  overflow: 'hidden',
  // Toast floats via `position: absolute` relative to the Island, so
  // the Island must establish a positioning context.
  position: 'relative',
  // Marker: marketplaceHostOf(el) looks for this flag walking up the
  // parent chain; first match wins.
  _marketplaceHost: true,

  // Bind the global Esc-to-close-detail listener. Idempotent — only
  // the first mount attaches the document listener; later mounts just
  // update `_lastMarketplaceHost` so Escape targets the latest host.
  // Also applies deep-linked URL filters + registers a popstate
  // listener so back/forward navigation re-applies URL state on
  // landing + standalone surfaces. (Canvas page uses its own
  // `root.currentSearch` reader instead and doesn't need these.)
  onCreate: (el) => {
    el.call('registerEscHandler', el.state)
    el.call('hydrateFiltersFromLocation', el.state)
    el.call('bindPopstateFilters', el.state)
  },
  // Sync host filter state back to the address bar on every change.
  // `writeFiltersToUrl` diff-gates so no replaceState fires when the
  // composed URL already matches — preventing the onUpdate → URL →
  // popstate → onUpdate loop. Canvas page has the equivalent already.
  onUpdate: (el) => {
    el.call('writeFiltersToUrl', el.state)
  },

  state: {
    // Multi-select: empty array === match every category. Clicking a
    // chip toggles membership; clicking `all` empties the array. See
    // `toggleCategory` in functions/index.js.
    activeCategories: [],
    activeKind: 'all',
    activePricing: 'all',
    activeSort: 'name',
    // 'comfortable' | 'compact' — hydrated from localStorage on mount.
    gridDensity: 'comfortable',
    query: '',
    selectedItem: null,
    // Stack of prior selectedItems for Back-button navigation when the
    // user clicks through "Similar items" rows. Cleared on Close (✕)
    // or grid-card click (a fresh journey). See `selectItemWithHistory`
    // helper in functions/index.js.
    selectedHistory: [],
    sidebarMode: false,
    installing: null,
    installed: [],
    // User-curated favorites — hydrated from localStorage on Grid mount.
    pinnedIds: [],
    // Recently-viewed item ids — capped at RECENT_LIMIT in
    // `trackRecent`; hydrated from localStorage on Grid mount.
    recentIds: [],
    // Transient toast — populated by `flashToast`, cleared after 4s.
    toastMessage: null,
    toastUndoItemId: null,
    // Toggled by `?` key via the global keydown listener; cleared by
    // Esc or by clicking the overlay backdrop.
    shortcutsOpen: false,
    catalog,
    // Install modal state — null when closed, { item, step, draft,
    //   preselectedKind?, defaultScope?, catalogFilter?, onInstalled?,
    //   domain?, _catalogError? } when open.
    installModal: null,
    _installModalSaving: false,
    _installModalError: null
  },

  // Composable chrome — consumers can override `showSidebar` /
  // `showFilters` / `showNavbar` via props to slim down the embed.
  MarketplaceNavbar: {
    hide: (el) => el.parent?.showNavbar === false
  },
  MarketplaceBody: {
    flow: 'x',
    flex: '1',
    overflow: 'hidden',
    minHeight: '0',
    MarketplaceSidebar: {
      hide: (el) => el.parent?.parent?.showSidebar === false
    },
    MarketplaceContent: {
      flow: 'y',
      flex: '1',
      overflow: 'hidden',
      MarketplaceFilters: {
        hide: (el) => el.parent?.parent?.parent?.showFilters === false
      },
      // Thin summary between filter chips and the grid. Same hide
      // gate as filters — embeds that opted out of filters also get
      // a cleaner layout.
      MarketplaceResultsBar: {
        hide: (el) => el.parent?.parent?.parent?.showFilters === false
      },
      MarketplaceGrid: {
        flex: '1',
        overflow: 'y auto'
      }
    },
    MarketplaceDetail: {}
  },

  // Transient feedback (install confirmation, Undo). Absolute-positioned
  // via `MarketplaceToast` so it overlays the body without reflowing it.
  MarketplaceToast: {},

  // Help overlay for keyboard shortcuts, toggled via `?` key.
  MarketplaceShortcutsOverlay: {},

  // Install modal — fixed overlay, gated on host.installModal. Sits at
  // the island level so it correctly uses `marketplaceHostOf` and stays
  // anchored to the island's state (not canvas root state).
  MarketplaceInstallModal: {}
}
